import { useState } from "react";
import { Link } from "react-router-dom";
import type { ReactNode } from "react";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { useThemeStore } from "@/shared/theme/useTheme";
import type { DashboardTask } from "@/modules/dashboard/components/DashboardSidebarCards";

/** A store the seller can switch to (subset of the session's availableStores). */
export type SidebarStore = {
  storeId: string;
  storeSlug: string;
  storeName: string;
  storeShort: string;
};

// ─────────────────────────────────────────────────────────────────────────────
// Desktop vertical sidebar (lg+). RTL-first: pinned to the inline-start edge
// (= right in Arabic) via logical properties. Light surface (Trioty redesign).
// The collapse control fully hides the sidebar (AppShell unmounts it and shows a
// toggle in the top bar to bring it back) — there is no icon-rail state.
// Presentation-only — all data/handlers come from AppShell. Structure:
// logo + hide → store card/switcher → grouped nav → Today's tasks → account.
// ─────────────────────────────────────────────────────────────────────────────

export type SidebarSubItem = {
  href: string;
  label: string;
  isActive: boolean;
};

/**
 * A nav row. Exactly one of `href` / `onClick` is set:
 *  - `href` → a destination; renders as a `<Link>` (`<a>`).
 *  - `onClick` → an in-place action with no route (e.g. "Share store", which
 *    opens the share sheet); renders as a `<button>`.
 * An action row is never `isActive` — there is no URL for it to match.
 */
export type SidebarNavItem = {
  /** Route target. Omit for action rows (see `onClick`). */
  href?: string;
  /** Action handler for route-less rows. Omit for destinations (see `href`). */
  onClick?: () => void;
  label: string;
  icon: ReactNode;
  isActive: boolean;
  /** Numeric badge (e.g. actionable orders). */
  count?: number;
  /** Small setup hint dot. */
  showHint?: boolean;
  /** When present, the row becomes an expandable group with a nested list. */
  subItems?: SidebarSubItem[];
};

export type SidebarGroup = {
  /** Omitted for the primary (un-headed) tier. */
  header?: string;
  items: SidebarNavItem[];
};

export type SidebarIdentity = {
  storeName: string;
  storeShort: string | null;
  initial: string;
  /** Uploaded store logo; falls back to the monogram initial when null. */
  logoUrl: string | null;
};

type SidebarProps = {
  dashboardHref: string;
  settingsHref: string;
  groups: SidebarGroup[];
  /** When true, the sidebar animates its width to 0 (fully hidden). */
  hidden: boolean;
  /** Hide the whole sidebar (header collapse button). */
  onHide: () => void;
  identity: SidebarIdentity;
  /** Stores the seller can switch between (from the session). */
  availableStores: SidebarStore[];
  /** Currently-active store slug. */
  activeStoreSlug: string;
  /** Switch to another store (wraps `useStoreSwitch`). */
  onSwitchStore: (slug: string) => void;
  /** True while a switch is in flight. */
  isSwitchingStore: boolean;
  /** "Today's tasks" rows (derived by `useSellerTasks`). */
  tasks: DashboardTask[];
  /** Card heading — "Complete setup" while onboarding, else "Today's tasks". */
  tasksTitle: string;
  /** Account menu (user-row trigger + dropdown), rendered in the footer. */
  accountSlot: ReactNode;
};

export function Sidebar({
  dashboardHref,
  settingsHref,
  groups,
  hidden,
  onHide,
  identity,
  availableStores,
  activeStoreSlug,
  onSwitchStore,
  isSwitchingStore,
  tasks,
  tasksTitle,
  accountSlot,
}: SidebarProps) {
  const t = useTranslations();
  const dark = useThemeStore((s) => s.resolvedDark);
  const appName = t(keys.common.appName);

  return (
    <aside
      aria-hidden={hidden || undefined}
      className={[
        "hidden h-full shrink-0 overflow-hidden bg-[color:var(--sidebar-surface)] transition-[width] duration-200 ease-out lg:block",
        hidden ? "border-e-0" : "border-e border-[color:var(--sidebar-border)]",
      ].join(" ")}
      style={{ width: hidden ? 0 : 264 }}
    >
      <div className="flex h-full w-[264px] flex-col overflow-hidden text-[color:var(--sidebar-ink)]">
      {/* Logo + hide toggle */}
      <div className="flex h-16 shrink-0 items-center justify-between px-4">
        <Link to={dashboardHref} aria-label={appName} className="flex items-center">
          <img
            src={dark ? "/logo-sidebar-dark.svg" : "/logo-sidebar.svg"}
            alt={appName}
            className="h-7 w-auto object-contain"
          />
        </Link>
        <button
          type="button"
          onClick={onHide}
          aria-label={t(keys.seller.navCollapse)}
          className="inline-flex h-8 w-8 items-center justify-center rounded-[8px] text-[color:var(--sidebar-ink-disabled)] transition-colors hover:bg-[color:var(--sidebar-hover-bg)] hover:text-[color:var(--sidebar-ink)]"
        >
          <CollapseIcon />
        </button>
      </div>

      {/* Store identity card — a switcher when the seller has >1 store,
          otherwise the Link-to-settings card. */}
      <div className="px-3">
        {availableStores.length > 1 ? (
          <StoreSwitcher
            identity={identity}
            settingsHref={settingsHref}
            availableStores={availableStores}
            activeStoreSlug={activeStoreSlug}
            onSwitchStore={onSwitchStore}
            isSwitchingStore={isSwitchingStore}
          />
        ) : (
          <StoreIdentityCard identity={identity} settingsHref={settingsHref} />
        )}
      </div>

      {/* Nav (scroll region) */}
      <nav className="no-scrollbar mt-2 flex-1 overflow-y-auto px-3 pb-2">
        {groups.map((group, gi) => (
          <div key={group.header ?? `primary-${gi}`} className={gi > 0 ? "mt-4" : ""}>
            {group.header ? (
              <div className="flex items-center gap-2 px-3 pb-1.5 pt-1 text-[11px] font-bold uppercase tracking-[0.04em] text-[color:var(--sidebar-ink-disabled)]">
                <span
                  aria-hidden="true"
                  className="inline-block h-[13px] w-[13px] rounded-full border-[1.5px] border-dashed border-[color:var(--border-strong)]"
                />
                {group.header}
              </div>
            ) : null}
            <ul className="flex flex-col gap-0.5">
              {group.items.map((item) => (
                <li key={item.href ?? item.label}>
                  {item.subItems ? (
                    <NavGroup item={item} />
                  ) : (
                    <NavRow item={item} hintLabel={t(keys.seller.navHintSetup)} />
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      {/* Today's tasks (or the setup checklist until onboarding is done) */}
      {tasks.length > 0 ? (
        <SidebarTasks tasks={tasks} title={tasksTitle} />
      ) : null}

      {/* Account footer */}
      <div className="shrink-0 border-t border-[color:var(--sidebar-border)] p-2">
        {accountSlot}
      </div>
      </div>
    </aside>
  );
}

// ─── Nav row ──────────────────────────────────────────────────────────────────

function NavRow({
  item,
  hintLabel,
}: {
  item: SidebarNavItem;
  hintLabel: string;
}) {
  const className = [
    "relative flex min-h-[40px] w-full items-center gap-3 rounded-[10px] px-3 text-[13.5px] transition-colors",
    item.isActive
      ? "bg-[color:var(--sidebar-active-bg)] font-semibold text-[color:var(--sidebar-active-ink)] shadow-[inset_0_0_0_1px_var(--pillring)]"
      : "font-medium text-[color:var(--sidebar-ink-muted)] hover:bg-[color:var(--sidebar-hover-bg)] hover:text-[color:var(--sidebar-ink)]",
  ].join(" ");

  const content = (
    <>
      <span className="inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center">
        {item.icon}
      </span>
      <span className="min-w-0 flex-1 truncate text-start">{item.label}</span>
      {item.count !== undefined ? (
        <span className="inline-flex min-w-[20px] items-center justify-center rounded-full bg-[color:var(--info-bg)] px-1.5 py-0.5 text-[11px] font-bold leading-none text-[color:var(--info-fg)]">
          {item.count}
        </span>
      ) : item.showHint ? (
        <span
          aria-label={hintLabel}
          className="h-1.5 w-1.5 shrink-0 rounded-full bg-[color:var(--gold-500)]"
        />
      ) : null}
    </>
  );

  // Route-less rows (e.g. "Share store") are actions, so they must be a
  // <button> — an <a> with no href is not focusable and lies to screen readers.
  if (item.href === undefined) {
    return (
      <button type="button" onClick={item.onClick} className={className}>
        {content}
      </button>
    );
  }

  return (
    <Link
      to={item.href}
      aria-current={item.isActive ? "page" : undefined}
      className={className}
    >
      {content}
    </Link>
  );
}

// ─── Expandable nav group (Store settings → sections) ─────────────────────────

function NavGroup({ item }: { item: SidebarNavItem }) {
  const [open, setOpen] = useState(item.isActive);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={[
          "relative flex min-h-[40px] w-full items-center gap-3 rounded-[10px] px-3 text-[13.5px] transition-colors",
          item.isActive
            ? "bg-[color:var(--sidebar-active-bg)] font-semibold text-[color:var(--sidebar-active-ink)] shadow-[inset_0_0_0_1px_var(--pillring)]"
            : "font-medium text-[color:var(--sidebar-ink-muted)] hover:bg-[color:var(--sidebar-hover-bg)] hover:text-[color:var(--sidebar-ink)]",
        ].join(" ")}
      >
        <span className="inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center">
          {item.icon}
        </span>
        <span className="min-w-0 flex-1 truncate text-start">{item.label}</span>
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className={`shrink-0 text-[color:var(--sidebar-ink-disabled)] transition-transform ${open ? "rotate-180" : ""}`}
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {open && item.subItems ? (
        <div className="my-0.5 ms-[21px] flex flex-col gap-0.5 border-s-[1.5px] border-[color:var(--line2)] ps-3">
          {item.subItems.map((sub) => (
            <Link
              key={sub.href}
              to={sub.href}
              aria-current={sub.isActive ? "page" : undefined}
              className={[
                "flex items-center gap-2.5 rounded-[9px] px-[11px] py-2 text-[13px] transition-colors",
                sub.isActive
                  ? "bg-[color:var(--sidebar-active-bg)] font-bold text-[color:var(--sidebar-active-ink)]"
                  : "font-medium text-[color:var(--sidebar-ink-muted)] hover:bg-[color:var(--sidebar-hover-bg)] hover:text-[color:var(--sidebar-ink)]",
              ].join(" ")}
            >
              <span
                aria-hidden="true"
                className="h-[5px] w-[5px] shrink-0 rounded-full"
                style={{ background: sub.isActive ? "var(--accent-500)" : "var(--sidebar-ink-disabled)" }}
              />
              {sub.label}
            </Link>
          ))}
        </div>
      ) : null}
    </>
  );
}

// ─── Today's tasks ────────────────────────────────────────────────────────────

function SidebarTasks({
  tasks,
  title,
}: {
  tasks: DashboardTask[];
  title: string;
}) {
  const t = useTranslations();
  const remaining = tasks.filter((task) => !task.done).length;
  return (
    <div className="mx-3 mb-2 shrink-0 rounded-[13px] border border-[color:var(--sidebar-border)] bg-[color:var(--sidebar-surface-elevated)] p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[11.5px] font-bold text-[color:var(--sidebar-ink)]">
          {title}
        </span>
        <span className="rounded-full bg-[color:var(--pill)] px-2 py-0.5 text-[10px] font-bold text-[color:var(--sidebar-ink-muted)]">
          {t(keys.dashboard.tasksRemaining, { count: remaining })}
        </span>
      </div>
      <div className="flex flex-col">
        {tasks.map((task, i) => {
          const inner = (
            <>
              {task.done ? (
                <span
                  aria-hidden="true"
                  className="flex h-[15px] w-[15px] shrink-0 items-center justify-center rounded-full bg-[color:var(--accent-500)] text-[color:var(--on-accent)]"
                >
                  <TaskCheckIcon />
                </span>
              ) : (
                <span
                  aria-hidden="true"
                  className="h-[15px] w-[15px] shrink-0 rounded-full border-2 border-[color:var(--sidebar-ink-disabled)]"
                />
              )}
              <span
                className={[
                  "min-w-0 flex-1 truncate text-[11.5px]",
                  task.done
                    ? "text-[color:var(--sidebar-ink-disabled)] line-through"
                    : "font-medium text-[color:var(--sidebar-ink-muted)]",
                ].join(" ")}
              >
                {task.title}
              </span>
              {task.urgent && !task.done ? (
                <span
                  aria-hidden="true"
                  className="h-1.5 w-1.5 shrink-0 rounded-full bg-[color:var(--error-500)]"
                />
              ) : null}
            </>
          );
          const key = `${task.title}-${i}`;
          const baseClass = "flex items-center gap-2.5 py-1";
          return task.href ? (
            <Link
              key={key}
              to={task.href}
              className={`${baseClass} -mx-1 rounded-md px-1 transition-colors hover:bg-[color:var(--sidebar-hover-bg)]`}
            >
              {inner}
            </Link>
          ) : (
            <div key={key} className={baseClass}>
              {inner}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Store identity card ──────────────────────────────────────────────────────

/** Store logo (or gradient monogram fallback) — shared by the card & switcher. */
function StoreMonogram({
  logoUrl,
  storeName,
  initial,
}: {
  logoUrl: string | null;
  storeName: string;
  initial: string;
}) {
  return logoUrl ? (
    <img
      src={logoUrl}
      alt={storeName}
      className="h-8 w-8 shrink-0 rounded-[9px] bg-white object-cover"
    />
  ) : (
    <div
      aria-hidden="true"
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] text-[12px] font-extrabold text-white"
      style={{ background: "linear-gradient(135deg, #1f2c4d, #2f4170)" }}
    >
      {initial}
    </div>
  );
}

/** Green "Live" dot with a pulsing ring. */
function LiveDot() {
  return (
    <span aria-hidden="true" className="relative inline-block h-1.5 w-1.5">
      <span className="absolute inset-0 rounded-full bg-[#16A34A]" />
      <span className="live-ping absolute inset-0 rounded-full bg-[#16A34A]" />
    </span>
  );
}

/** Identity content (monogram + name + short + Live) shared by the settings
 * card and the switcher trigger. `trailing` renders the switcher caret. */
function StoreIdentityBody({
  identity,
  trailing,
}: {
  identity: SidebarIdentity;
  trailing?: ReactNode;
}) {
  const t = useTranslations();
  const { storeName, storeShort, initial, logoUrl } = identity;
  return (
    <div className="flex items-center gap-2.5">
      <StoreMonogram logoUrl={logoUrl} storeName={storeName} initial={initial} />
      <div className="min-w-0 flex-1">
        <div className="truncate text-[13px] font-bold leading-tight text-[color:var(--sidebar-ink)]">
          {storeName}
        </div>
        <div className="mt-0.5 flex items-center gap-1.5 text-[10.5px] font-medium text-[color:var(--sidebar-ink-disabled)]">
          {storeShort ? (
            <span className="font-mono tracking-[0.04em]">{storeShort}</span>
          ) : null}
          <span aria-hidden="true" className="text-[color:var(--border-strong)]">
            •
          </span>
          <span className="inline-flex items-center gap-1 font-semibold text-[color:var(--success-500)]">
            <LiveDot />
            {t(keys.seller.storeLiveLabel)}
          </span>
        </div>
      </div>
      {trailing}
    </div>
  );
}

function StoreIdentityCard({
  identity,
  settingsHref,
}: {
  identity: SidebarIdentity;
  settingsHref: string;
}) {
  const t = useTranslations();

  return (
    <Link
      to={settingsHref}
      aria-label={t(keys.seller.storeSettingsTitle)}
      className="block rounded-[13px] border border-[color:var(--sidebar-border)] bg-[color:var(--sidebar-surface-elevated)] px-3 py-2.5 transition-colors hover:border-[color:var(--border-strong)]"
    >
      <StoreIdentityBody identity={identity} />
    </Link>
  );
}

// ─── Store switcher (multi-store) ─────────────────────────────────────────────

function StoreSwitcher({
  identity,
  settingsHref,
  availableStores,
  activeStoreSlug,
  onSwitchStore,
  isSwitchingStore,
}: {
  identity: SidebarIdentity;
  settingsHref: string;
  availableStores: SidebarStore[];
  activeStoreSlug: string;
  onSwitchStore: (slug: string) => void;
  isSwitchingStore: boolean;
}) {
  const t = useTranslations();
  const switchLabel = t(keys.seller.storeSwitcherSwitch);

  return (
    <Menu as="div" className="relative">
      {({ open }) => (
        <>
          <MenuButton
            aria-label={switchLabel}
            disabled={isSwitchingStore}
            className={[
              "block w-full rounded-[13px] border px-3 py-2.5 text-start transition-colors focus:outline-none",
              "border-[color:var(--sidebar-border)] bg-[color:var(--sidebar-surface-elevated)]",
              "hover:border-[color:var(--border-strong)]",
              open ? "border-[color:var(--border-strong)]" : "",
              isSwitchingStore ? "cursor-wait opacity-70" : "",
            ].join(" ")}
          >
            <StoreIdentityBody
              identity={identity}
              trailing={<SwitcherCaret open={open} />}
            />
          </MenuButton>

          <MenuItems
            anchor="bottom start"
            className="z-50 mt-2 w-[248px] origin-top overflow-hidden rounded-[14px] border border-[color:var(--border-light)] bg-[color:var(--surface)] p-1.5 shadow-[0_24px_60px_rgba(15,20,40,0.16),0_4px_12px_rgba(15,20,40,0.06)] focus:outline-none"
          >
            {availableStores.map((store) => {
              const isActive = store.storeSlug === activeStoreSlug;
              return (
                <MenuItem key={store.storeId}>
                  <button
                    type="button"
                    disabled={isSwitchingStore}
                    aria-current={isActive ? "true" : undefined}
                    onClick={() => {
                      if (!isActive) onSwitchStore(store.storeSlug);
                    }}
                    className={[
                      "flex w-full items-center gap-2.5 rounded-[9px] px-2.5 py-2 text-start transition-colors",
                      "data-[focus]:bg-[color:var(--surface-alt)]",
                      isSwitchingStore ? "cursor-wait opacity-60" : "",
                    ].join(" ")}
                  >
                    <span
                      aria-hidden="true"
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] text-[13px] font-bold text-white"
                      style={{
                        background: "linear-gradient(135deg, #1f2c4d, #2f4170)",
                      }}
                    >
                      {(store.storeName || "?").trim().charAt(0).toUpperCase()}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13.5px] font-semibold leading-tight text-[color:var(--text-primary)]">
                        {store.storeName}
                      </span>
                      {store.storeShort ? (
                        <span className="mt-0.5 block truncate font-mono text-[11px] tracking-[0.04em] text-[color:var(--text-muted)]">
                          {store.storeShort}
                        </span>
                      ) : null}
                    </span>
                    {isActive ? (
                      <span className="inline-flex shrink-0 items-center gap-1 text-[11px] font-semibold text-[color:var(--success-500)]">
                        <CheckMarkIcon />
                        {t(keys.seller.storeSwitcherCurrent)}
                      </span>
                    ) : null}
                  </button>
                </MenuItem>
              );
            })}

            <div aria-hidden="true" className="my-1 h-px bg-[color:var(--border-light)]" />

            <MenuItem>
              <Link
                to={settingsHref}
                className="flex w-full items-center gap-2.5 rounded-[9px] px-2.5 py-2 text-start text-[13.5px] font-medium text-[color:var(--text-primary)] transition-colors data-[focus]:bg-[color:var(--surface-alt)]"
              >
                <span
                  aria-hidden="true"
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] bg-[color:var(--surface-alt)] text-[color:var(--text-secondary)]"
                >
                  <SwitcherSettingsIcon />
                </span>
                {t(keys.seller.storeSettingsTitle)}
              </Link>
            </MenuItem>
          </MenuItems>
        </>
      )}
    </Menu>
  );
}

function SwitcherCaret({ open }: { open: boolean }) {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={[
        "shrink-0 text-[color:var(--sidebar-ink-disabled)] transition-transform duration-150",
        open ? "text-[color:var(--sidebar-ink)]" : "",
      ].join(" ")}
    >
      <path d="m8 9 4-4 4 4" />
      <path d="m16 15-4 4-4-4" />
    </svg>
  );
}

function CheckMarkIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.4}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function TaskCheckIcon() {
  return (
    <svg
      width="9"
      height="9"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={3.4}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function CollapseIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="3" width="18" height="18" rx="2.5" />
      <path d="M9 3v18" />
    </svg>
  );
}

function SwitcherSettingsIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

// ─── Icons ────────────────────────────────────────────────────────────────────

const ICON = {
  width: 22,
  height: 22,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor" as const,
  strokeWidth: 1.7,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

export function DashboardSidebarIcon() {
  return (
    <svg {...ICON}>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 9h18M9 21V9" />
    </svg>
  );
}
export function OrdersSidebarIcon() {
  return (
    <svg {...ICON}>
      <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" />
      <path d="M3 6h18" />
      <path d="M16 10a4 4 0 0 1-8 0" />
    </svg>
  );
}
export function ProductsSidebarIcon() {
  return (
    <svg {...ICON}>
      <path d="M21 8 12 3 3 8v8l9 5 9-5V8Z" />
      <path d="m3 8 9 5 9-5" />
      <path d="M12 13v8" />
    </svg>
  );
}
export function CategoriesSidebarIcon() {
  return (
    <svg {...ICON}>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
    </svg>
  );
}
export function CouponsSidebarIcon() {
  return (
    <svg {...ICON}>
      <path d="M19 5 5 19" />
      <circle cx="6.5" cy="6.5" r="2.5" />
      <circle cx="17.5" cy="17.5" r="2.5" />
    </svg>
  );
}
export function ReviewsSidebarIcon() {
  return (
    <svg {...ICON}>
      <path d="m12 3 2.9 5.9 6.1.9-4.5 4.3 1 6.1L12 17.8 6.5 20.2l1-6.1L3 9.8l6.1-.9L12 3Z" />
    </svg>
  );
}
export function CheckoutLinksSidebarIcon() {
  return (
    <svg {...ICON}>
      <path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7" />
      <path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7" />
    </svg>
  );
}
export function StoreEditorSidebarIcon() {
  return (
    <svg {...ICON}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z" />
    </svg>
  );
}
export function PaymentSidebarIcon() {
  return (
    <svg {...ICON}>
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <path d="M2 10h20" />
      <path d="M6 15h4" />
    </svg>
  );
}
export function SettingsSidebarIcon() {
  return (
    <svg {...ICON}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}
export function ProfileSidebarIcon() {
  return (
    <svg {...ICON}>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
    </svg>
  );
}

export function TransactionsSidebarIcon() {
  return (
    <svg {...ICON}>
      <rect x="2" y="5" width="20" height="14" rx="2.5" />
      <path d="M2 10h20" />
      <path d="M6 15h3" />
      <path d="M13 15h5" />
    </svg>
  );
}

export function ShareSidebarIcon() {
  return (
    <svg {...ICON}>
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4" />
    </svg>
  );
}

export function CustomersSidebarIcon() {
  return (
    <svg {...ICON}>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    </svg>
  );
}
